/**
 * CitationPill — inline pill rendered for each
 * `[[spine:<persona_id>:<message_id>]]` token in a synth response.
 *
 * Hover surfaces a cross-panel glow on the matching chatmap spine
 * root (J.2 wires the reader side). Click body snaps the Lens to the
 * cited persona's trail + focuses the bubble. Reply sub-button does
 * the same Lens snap without the bubble focus — operator's next
 * compose targets that persona's trail (Multi-Lens picker write-target
 * resolution from SP3 H.2/H.3).
 */
import React, { useContext } from 'react';
import { PersonaChatUIContext } from '../../contexts/PersonaChatUIContext';
import { LensContext } from '../../contexts/LensContext';


interface CitationPillProps {
  personaId: string;
  spineId: string;
  personaName?: string;
}


export const CitationPill: React.FC<CitationPillProps> = ({
  personaId, spineId, personaName,
}) => {
  const uiCtx = useContext(PersonaChatUIContext);
  const lensCtx = useContext(LensContext);

  const handleHover = () => uiCtx?.setCitationGlow?.(spineId);
  const handleLeave = () => uiCtx?.setCitationGlow?.(null);

  const handleBodyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Click body → focus the spine-root bubble AND snap Lens to persona
    uiCtx?.focusBubble?.(spineId);
    lensCtx?.setLens?.({
      mode: 'single',
      activePersonaIds: new Set([personaId]),
    });
  };

  const handleReplyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Reply sub-button: Lens snap only (no bubble focus). Operator's
    // next compose targets this persona's trail via SP3 H.3 picker
    // write-target resolution.
    lensCtx?.setLens?.({
      mode: 'single',
      activePersonaIds: new Set([personaId]),
    });
  };

  return (
    <span
      data-citation-pill={spineId}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 text-xs
                 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300
                 rounded border border-indigo-200 dark:border-indigo-700
                 cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-900/50
                 transition-colors"
      onMouseEnter={handleHover}
      onMouseLeave={handleLeave}
      onClick={handleBodyClick}
      title={`Spine root of ${personaName || personaId}`}
    >
      <span>◯ {personaName || personaId} spine</span>
      <span
        onClick={handleReplyClick}
        className="text-indigo-500 hover:text-indigo-700
                   dark:hover:text-indigo-100 font-medium"
      >
        · Reply
      </span>
    </span>
  );
};
